import { type UseQueryOptions, type UseQueryResult, useQuery } from '@tanstack/react-query';

import { useQueryStateAppContext } from '@/packages/query-state/shared/context/appContext';

import { type TCianApiSiteV1GetUserDataByRequestResponse_1 } from '@/repositories/generated/shared/monolith-python/cian-api/site/v1/get-user-data-by-request';

import { type QueryError, type QueryKey, getQueryKey } from './internal/config';
import { fetchCianApiSiteV1GetUserDataByRequestService } from './internal/fetchService';
import { userCianApiSiteV1GetUserDataByRequestSelector, userIsAuthenticatedSelector } from './selectors';

type UseCianApiSiteV1GetUserDataByRequestQueryOptions<TData> = UseQueryOptions<
  TCianApiSiteV1GetUserDataByRequestResponse_1,
  QueryError,
  TData,
  QueryKey
>;

export function useCianApiSiteV1GetUserDataByRequestQuery<TData = TCianApiSiteV1GetUserDataByRequestResponse_1>(
  options?: UseCianApiSiteV1GetUserDataByRequestQueryOptions<TData>,
): UseQueryResult<TData, QueryError> {
  const context = useQueryStateAppContext();

  return useQuery<TCianApiSiteV1GetUserDataByRequestResponse_1, QueryError, TData, QueryKey>({
    queryFn: () => fetchCianApiSiteV1GetUserDataByRequestService(context),
    queryKey: getQueryKey(),
    ...options,
  });
}

type UseUserQueryOptions = Omit<
  UseCianApiSiteV1GetUserDataByRequestQueryOptions<TCianApiSiteV1GetUserDataByRequestResponse_1['data']['user'] | null>,
  'select'
>;

export function useUserQuery(
  options?: UseUserQueryOptions,
): UseQueryResult<TCianApiSiteV1GetUserDataByRequestResponse_1['data']['user'] | null, QueryError> {
  return useCianApiSiteV1GetUserDataByRequestQuery({
    select: userCianApiSiteV1GetUserDataByRequestSelector,
    ...options,
  });
}

type UseIsUserAuthenticatedQueryOptions = Omit<
  UseCianApiSiteV1GetUserDataByRequestQueryOptions<boolean>,
  'select' | 'notifyOnChangeProps'
>;

export function useIsUserAuthenticatedQuery(
  options?: UseIsUserAuthenticatedQueryOptions,
): UseQueryResult<boolean, QueryError> {
  return useCianApiSiteV1GetUserDataByRequestQuery({
    notifyOnChangeProps: ['data'],
    select: userIsAuthenticatedSelector,
    ...options,
  });
}
